import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { AlertTriangle, AlertCircle, Wand2 } from 'lucide-react';

/**
 * CompileErrorDialog
 * Shows validation issues returned by the backend `/api/compile/validate`
 * endpoint and lets the user opt into additive auto-fixes before compiling.
 *
 * Props:
 *  - open: boolean
 *  - report: { valid, issues, fixes, ... } | null
 *  - onClose: () => void                 // user dismissed (cancel)
 *  - onApplyFixes: (fixIds: string[]) => void  // user clicked "Apply & Compile"
 *  - onCompileAnyway: () => void         // user wants to compile despite warnings
 *  - isProcessing: boolean
 */
const SEVERITY_ORDER = { error: 0, warning: 1 };

export default function CompileErrorDialog({
  open,
  report,
  onClose,
  onApplyFixes,
  onCompileAnyway,
  isProcessing = false,
}) {
  const [selectedFixes, setSelectedFixes] = useState({});

  useEffect(() => {
    if (open && report?.fixes) {
      // Pre-select every available fix by default — purely additive.
      const next = {};
      report.fixes.forEach((f) => {
        next[f.id] = true;
      });
      setSelectedFixes(next);
    }
  }, [open, report]);

  const errors = useMemo(
    () => (report?.issues || []).filter((i) => i.severity === 'error'),
    [report]
  );
  const warnings = useMemo(
    () => (report?.issues || []).filter((i) => i.severity === 'warning'),
    [report]
  );
  const sortedIssues = useMemo(() => {
    return [...(report?.issues || [])].sort(
      (a, b) =>
        (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9)
    );
  }, [report]);

  const fixIds = Object.keys(selectedFixes).filter((id) => selectedFixes[id]);
  const hasErrors = errors.length > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="compile-error-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {hasErrors ? (
              <AlertCircle className="h-5 w-5 text-red-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            )}
            {hasErrors ? 'Compile errors found' : 'Compile warnings'}
          </DialogTitle>
          <DialogDescription>
            {hasErrors
              ? 'Your Twee code has issues that will prevent a clean compile. Pick the auto-fixes you want to apply, or cancel to edit your code.'
              : 'Your Twee code compiles, but a few things could be improved. Pick any auto-fixes you want before compiling.'}
          </DialogDescription>
        </DialogHeader>

        {/* Issues list */}
        <div
          className="border rounded-md divide-y bg-slate-50"
          data-testid="compile-error-issue-list"
        >
          {sortedIssues.length === 0 && (
            <div className="p-4 text-sm text-slate-500">
              No issues found.
            </div>
          )}
          {sortedIssues.map((issue, idx) => (
            <div
              key={`${issue.code}-${idx}`}
              className="p-3 flex items-start gap-2 text-sm"
              data-testid={`compile-error-issue-${issue.code}`}
            >
              {issue.severity === 'error' ? (
                <AlertCircle className="h-4 w-4 mt-0.5 text-red-600 shrink-0" />
              ) : (
                <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
              )}
              <div className="flex-1">
                <div className="text-slate-800">{issue.message}</div>
                {(issue.passage || issue.line) && (
                  <div className="text-xs text-slate-500 mt-0.5">
                    {issue.passage && <>Passage: <code>{issue.passage}</code></>}
                    {issue.passage && issue.line ? ' · ' : ''}
                    {issue.line && <>Line {issue.line}</>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Fixes list */}
        {(report?.fixes || []).length > 0 && (
          <div className="mt-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-2">
              <Wand2 className="h-4 w-4 text-indigo-600" />
              Suggested auto-fixes
              <span className="text-xs font-normal text-slate-500">
                (additive — your existing code is preserved)
              </span>
            </div>
            <div className="space-y-2">
              {report.fixes.map((fix) => (
                <label
                  key={fix.id}
                  className="flex items-start gap-3 p-3 border rounded-md cursor-pointer hover:bg-slate-50"
                  data-testid={`compile-error-fix-${fix.id}`}
                >
                  <Checkbox
                    checked={!!selectedFixes[fix.id]}
                    onCheckedChange={(v) =>
                      setSelectedFixes((s) => ({ ...s, [fix.id]: !!v }))
                    }
                    data-testid={`compile-error-fix-checkbox-${fix.id}`}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-800">
                      {fix.label}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      {fix.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isProcessing}
            data-testid="compile-error-cancel-btn"
          >
            Cancel & edit code
          </Button>
          {!hasErrors && (
            <Button
              type="button"
              variant="secondary"
              onClick={onCompileAnyway}
              disabled={isProcessing}
              data-testid="compile-error-compile-anyway-btn"
            >
              Compile anyway
            </Button>
          )}
          <Button
            type="button"
            onClick={() => onApplyFixes(fixIds)}
            disabled={isProcessing || fixIds.length === 0}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="compile-error-apply-fixes-btn"
          >
            {fixIds.length > 0
              ? `Apply ${fixIds.length} fix${fixIds.length === 1 ? '' : 'es'} & compile`
              : 'Apply & compile'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
